import { createContext, useContext, type ReactNode } from 'react';

/**
 * Tenant- and form-scoped defaults that any field can read. The third
 * FIELDS.md seam: forms wrap their children in a provider populated from
 * Institution config, and fields consume it without prop-drilling.
 *
 * All entries are optional — every field ships a hard fallback for the case
 * where no provider is mounted (unit tests, isolated storybook, first-boot
 * institution with no structure yet).
 */
export interface FieldDefaults {
  /** Grade options as configured under Institution structure. */
  grade?: { options: readonly { value: string; label: string }[] };
  /** Section options resolved per-grade. */
  section?: { optionsFor: (grade: string | undefined) => readonly { value: string; label: string }[] };
  /** Default dial code, taken from Institution country/timezone. */
  phone?: { defaultCountry: string };
  /** Relation options; falls back to Father / Mother / Guardian. */
  relation?: { options: readonly { value: string; label: string }[] };
  /** Admission-number pattern hint shown as placeholder. */
  admissionNumber?: { pattern: string };
}

const Ctx = createContext<FieldDefaults>({});

export function FieldDefaultsProvider({ value, children }: { value: FieldDefaults; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFieldDefaults(): FieldDefaults {
  return useContext(Ctx);
}
